import { PetManager, LocalPetSprite } from "./PetManager";
import { listLocalPets, type LocalPetRecord } from "../lib/local-pets";
import { PawPrint, Check, Loader2, RotateCcw, Settings, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { approvedProbabilityLabel, createDemoGameClient, createRemoteGameClient, type GameClient } from "../lib/game-client";
import {
  DRAW_COST,
  RARITIES,
  availableDuplicateCount,
  formatTokenCount,
  itemCatalog,
  rarityDefinition,
  type GameInventoryItem,
  type GameReward,
  type GameState,
} from "../lib/game";
import type { LocalTokenSummary } from "../lib/local-usage";

type DrawPhase = "idle" | "loading" | "checked" | "countdown";

function initialDemoTokens(): number {
  const value = Number(new URLSearchParams(window.location.search).get("demoTokens"));
  return Number.isSafeInteger(value) && value >= 0 ? value : 1_500_000;
}

function useGameClient(): { client: GameClient; desktop: boolean; demo: boolean } {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const desktop = params.get("desktop") === "1" || Boolean(window.desktopPet);
    const demo = desktop || params.get("demo") === "1" || import.meta.env.DEV;
    return { client: demo ? createDemoGameClient(initialDemoTokens()) : createRemoteGameClient(), desktop, demo };
  }, []);
}

function stateWithReward(current: GameState, reward: GameReward): GameState {
  const inventory = current.inventory.filter((item) => item.id !== reward.item.id).concat(reward.item);
  return {
    ...current,
    serverTime: new Date().toISOString(),
    wallet: { ...current.wallet, spent: current.wallet.earned - reward.balance, balance: reward.balance },
    draw: {
      ...current.draw,
      nextEligibleAt: reward.nextEligibleAt,
      eligible: reward.balance >= current.draw.cost && (!reward.nextEligibleAt || Date.parse(reward.nextEligibleAt) <= Date.now()),
    },
    inventory,
  };
}

function formatCooldown(target: string | null, now: number): string {
  if (!target) return "30:00";
  const seconds = Math.max(0, Math.ceil((Date.parse(target) - now) / 1_000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export function GamePage() {
  const { client, desktop, demo } = useGameClient();
  const [state, setState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [activePet, setActivePet] = useState<LocalPetRecord | null>(null);
  useEffect(() => {
    let live = true;
    try {
      const id = localStorage.getItem("meterling-active-pet");
      if (id) void listLocalPets().then(pets => { if (live) setActivePet(pets.find(pet => pet.id === id) ?? null); }).catch(() => {});
    } catch { /* Default pet remains available when storage is unavailable. */ }
    return () => { live = false; };
  }, []);
  const selectPet = (pet: LocalPetRecord | null) => {
    try {
      if (pet) localStorage.setItem("meterling-active-pet", pet.id);
      else localStorage.removeItem("meterling-active-pet");
      setActivePet(pet);
    } catch { setError("无法保存桌宠选择，请检查本地存储空间。"); }
  };
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);
  const [drawPhase, setDrawPhase] = useState<DrawPhase>("idle");
  const [reward, setReward] = useState<GameReward | null>(null);
  const [floatingCost, setFloatingCost] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [localUsage, setLocalUsage] = useState<LocalTokenSummary | null>(null);
  const [alwaysOnTop, setAlwaysOnTop] = useState(true);
  const timers = useRef<number[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setState(await client.state()); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "无法读取游戏状态"); }
    finally { setLoading(false); }
  }, [client]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => () => timers.current.forEach(window.clearTimeout), []);
  useEffect(() => {
    document.documentElement.classList.toggle("desktop-pet-runtime", desktop);
    return () => document.documentElement.classList.remove("desktop-pet-runtime");
  }, [desktop]);
  useEffect(() => {
    window.desktopPet?.setExpanded(settingsOpen || managerOpen);
  }, [settingsOpen, managerOpen]);
  useEffect(() => {
    if (!desktop || !window.desktopPet) return;
    let active = true;
    const refresh = async () => {
      try {
        const summary = await window.desktopPet!.getLocalUsage();
        if (!active) return;
        setLocalUsage(summary);
        if (client.syncEarned) setState(await client.syncEarned(summary.effectiveTokens));
      } catch { /* keep the last reliable snapshot */ }
    };
    void window.desktopPet.getPreferences().then((preferences) => {
      if (active) setAlwaysOnTop(preferences.alwaysOnTop);
    });
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [client, desktop]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSettings();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const closeSettings = () => {
    setManagerOpen(false);
    setSettingsOpen(false);
    setExchangeOpen(false);
    setSelection([]);
  };

  const eligible = Boolean(state?.draw.eligible && drawPhase === "idle");
  const showBubble = eligible || drawPhase !== "idle";
  const displayedBalance = Math.max(0, (state?.wallet.balance ?? 0) - (drawPhase === "loading" ? DRAW_COST : 0));

  const runDraw = async () => {
    if (!state || !eligible) return;
    setDrawPhase("loading");
    setFloatingCost(true);
    setReward(null);
    setError(null);
    timers.current.push(window.setTimeout(() => setFloatingCost(false), 900));
    try {
      const result = await client.draw(crypto.randomUUID());
      setState((current) => current ? stateWithReward(current, result) : current);
      setReward(result);
      setDrawPhase("checked");
      timers.current.push(window.setTimeout(() => setDrawPhase("countdown"), 450));
      timers.current.push(window.setTimeout(() => setDrawPhase("idle"), 1_450));
    } catch (cause) {
      setDrawPhase("idle");
      setError(cause instanceof Error ? cause.message : "抽奖失败，请重试");
    }
  };

  const inventoryById = useMemo(() => new Map(state?.inventory.map((item) => [item.id, item]) ?? []), [state]);
  const selectedItems = selection.map((id) => inventoryById.get(id)).filter((item): item is GameInventoryItem => Boolean(item));
  const selectedRarity = selectedItems[0]?.rarity;
  const canExchange = selection.length === 10 && selectedItems.every((item) => item.rarity === selectedRarity) && rarityDefinition(selectedRarity!).next !== null;

  const selectForExchange = (item: GameInventoryItem) => {
    if (!exchangeOpen || selection.length >= 10 || item.rarity === "legendary") return;
    if (selectedRarity && selectedRarity !== item.rarity) return;
    const alreadySelected = selection.filter((id) => id === item.id).length;
    if (alreadySelected >= availableDuplicateCount(item)) return;
    setSelection((current) => [...current, item.id]);
  };

  const runExchange = async () => {
    if (!state || !canExchange) return;
    setError(null);
    try {
      const result = await client.exchange(selection, crypto.randomUUID());
      const refreshed = await client.state();
      setState(refreshed);
      setReward(result);
      setSelection([]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "兑换失败，请重试");
    }
  };

  const resetDemo = async () => {
    if (!client.reset) return;
    setState(await client.reset());
    setReward(null);
    setSelection([]);
    setDrawPhase("idle");
    setError(null);
  };

  if (loading) return <main className={`game-page ${desktop ? "desktop" : "web"}`}><div className="game-loading"><Loader2 className="spin" /> 正在唤醒桌宠…</div></main>;

  return (
    <main className={`game-page ${desktop ? "desktop" : "web"}`} data-testid="game-page">
      {!desktop && <div className="game-demo-ribbon">{demo ? "LOCAL DEMO" : "LIVE"}</div>}
      <section className="pet-hud" aria-label="桌面宠物与 Token 状态">
        {reward && <div className={`reward-toast rarity-${reward.item.rarity}`} role="status">
          <span className="reward-emoji">{reward.item.emoji}</span>
          <strong>{reward.isNew ? "新物品" : "重复物品"}</strong>
          <small>{reward.item.label} · ×{reward.item.quantity}</small>
        </div>}
        {floatingCost && <div className="floating-cost">-100,000</div>}
        <button className="pet-character drag-region" type="button" title="拖动桌宠" aria-label={activePet?.manifest.displayName ?? "黑猫桌宠"}>
          <span className="pet-crown">♔</span>
          {activePet ? <LocalPetSprite pet={activePet} width={140} /> : <span className="pet-emoji">🐈‍⬛</span>}
          <span className="pet-shadow" />
        </button>
        <div className="hud-controls no-drag">
          {showBubble && (
            <button className={`draw-bubble phase-${drawPhase}`} type="button" onClick={() => void runDraw()} disabled={!eligible} aria-label="消耗100,000 Token抽取物品">
              {drawPhase === "loading" ? <Loader2 className="spin" /> : drawPhase === "checked" ? <Check /> : drawPhase === "countdown" ? <span>{formatCooldown(state?.draw.nextEligibleAt ?? null, now)}</span> : <><span className="bubble-gift">🎁</span><strong>100,000</strong></>}
            </button>
          )}
          <output className="token-counter" aria-label="剩余Token">{formatTokenCount(displayedBalance)}</output>
          <button className="game-settings-button" type="button" onClick={() => setManagerOpen(true)} aria-label="管理桌宠"><PawPrint size={22} /></button>
          <button className="game-settings-button" type="button" onClick={() => setSettingsOpen(true)} aria-label="打开设置和物品栏"><Settings size={22} /></button>
        </div>
        {error && <div className="game-error" role="alert">{error}</div>}
      </section>

      {managerOpen && <div className="game-overlay no-drag"><section className="inventory-window pet-manager-window" role="dialog" aria-modal="true" aria-label="桌宠管理"><header className="inventory-header"><div><small>TOKEN RAIN · PETS</small><h2>桌宠管理</h2></div><button className="pixel-close" onClick={() => setManagerOpen(false)} aria-label="关闭桌宠管理"><X /></button></header><PetManager activeId={activePet?.id ?? null} onSelect={selectPet} /></section></div>}
      {settingsOpen && state && (
        <div className="game-overlay no-drag" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeSettings(); }}>
          <section className={`inventory-window ${exchangeOpen ? "with-contract" : ""}`} role="dialog" aria-modal="true" aria-label="物品栏">
            <header className="inventory-header">
              <div><small>METERLING · COLLECTION</small><h2>物品栏</h2></div>
              <button type="button" className="pixel-close" onClick={closeSettings} aria-label="关闭物品栏"><X /></button>
            </header>
            {desktop && localUsage && (
              <section className="usage-summary" aria-label="本地 AI Token 统计">
                <div className="usage-summary-head">
                  <div><small>LOCAL TOKEN METER</small><strong>{formatTokenCount(localUsage.effectiveTokens)}</strong></div>
                  <div className="usage-periods"><span>今日 <b>{formatTokenCount(localUsage.todayEffectiveTokens)}</b></span><span>7日 <b>{formatTokenCount(localUsage.last7DaysEffectiveTokens)}</b></span></div>
                </div>
                <div className="usage-tool-grid">
                  {localUsage.tools.map((tool) => (
                    <article key={tool.id} className={tool.detected ? "usage-tool detected" : "usage-tool"}>
                      <header><span>{tool.id === "codex" ? "⌘" : "✦"}</span><strong>{tool.label}</strong><i>{tool.detected ? "已检测" : "无记录"}</i></header>
                      <b>{formatTokenCount(tool.effectiveTokens)}</b>
                      <dl><div><dt>输入</dt><dd>{formatTokenCount(tool.input)}</dd></div><div><dt>输出</dt><dd>{formatTokenCount(tool.output)}</dd></div><div><dt>缓存写入</dt><dd>{formatTokenCount(tool.cacheWrite)}</dd></div><div><dt>缓存读取</dt><dd>{formatTokenCount(tool.cacheRead)}</dd></div></dl>
                    </article>
                  ))}
                </div>
                <label className="desktop-preference"><input type="checkbox" checked={alwaysOnTop} onChange={(event) => { const enabled = event.target.checked; setAlwaysOnTop(enabled); window.desktopPet?.setAlwaysOnTop(enabled); }} /><span>始终置顶</span><small>可关闭以避免与 Codex 原生宠物重叠</small></label>
                <p>{localUsage.demo ? "当前为固定余额验收模式。" : "从本机日志提取 Token 计数，不保存或上传代码与对话内容。有效 Token = 输入 + 输出 + 缓存写入。"}</p>
              </section>
            )}
            <div className="rarity-legend">
              {RARITIES.map((rarity) => <span key={rarity.id} style={{ "--rarity-color": rarity.color } as React.CSSProperties}>{rarity.label}<small>{(rarity.probabilityBps / 100).toFixed(2)}%</small></span>)}
            </div>
            <div className="inventory-grid" aria-label="已获得物品">
              {itemCatalog().map((catalogItem) => {
                const item = inventoryById.get(catalogItem.id);
                if (!item) return null;
                const rarity = rarityDefinition(item.rarity);
                const selectedCount = selection.filter((id) => id === item.id).length;
                const disabled = exchangeOpen && (item.rarity === "legendary" || Boolean(selectedRarity && selectedRarity !== item.rarity) || selectedCount >= availableDuplicateCount(item));
                return (
                  <button key={item.id} type="button" className={`inventory-item rarity-${item.rarity} ${disabled ? "disabled" : ""}`} onClick={() => selectForExchange(item)} aria-label={`${item.label}，拥有${item.quantity}件`}>
                    <span className="item-emoji">{item.emoji}</span><span className="item-number">{item.id}</span>
                    {item.quantity > 1 && <span className="item-count">{item.quantity}</span>}
                    {selectedCount > 0 && <span className="item-selected">-{selectedCount}</span>}
                  </button>
                );
              })}
            </div>
            <footer className="inventory-footer">
              <button type="button" className={exchangeOpen ? "pixel-button secondary" : "pixel-button"} onClick={() => { setExchangeOpen((open) => !open); setSelection([]); }}>{exchangeOpen ? "返回物品栏" : "EXCHANGE"}</button>
              {demo && <button type="button" className="demo-reset" onClick={() => void resetDemo()}><RotateCcw size={14} /> 重置演示</button>}
            </footer>
          </section>

          {exchangeOpen && (
            <section className="contract-window" aria-label="淘汰合同">
              <header><div><small>ELIMINATION CONTRACT</small><h2>淘汰合同</h2></div><button type="button" className="pixel-close" onClick={() => { setExchangeOpen(false); setSelection([]); }}><X /></button></header>
              <div className="contract-slots">
                {Array.from({ length: 10 }, (_, index) => {
                  const item = selectedItems[index];
                  return <button type="button" key={index} className={item ? `contract-slot filled rarity-${item.rarity}` : "contract-slot"} onClick={() => item && setSelection((current) => current.filter((_, position) => position !== index))}>{item ? <><span>{item.emoji}</span><small>{item.id}</small></> : null}</button>;
                })}
              </div>
              <button type="button" className="exchange-submit" disabled={!canExchange} onClick={() => void runExchange()}>{selection.length === 10 ? "兑换下一品质" : `${selection.length} / 10`}</button>
              <p>使用10件相同品质的重复物品，随机兑换1件下一品质物品。投入物品将永久消失。</p>
              {selectedRarity && <div className="contract-rule"><span>{rarityDefinition(selectedRarity).label}</span><b>×10</b><i>→</i><span>{rarityDefinition(rarityDefinition(selectedRarity).next!).label}</span><b>×1</b></div>}
            </section>
          )}
        </div>
      )}

      {!desktop && <footer className="game-probability">{approvedProbabilityLabel()}</footer>}
    </main>
  );
}
